import { useLayoutEffect, useRef } from 'react';
import { useWizardStore } from '../../store/wizard.js';
import { ChatMessage } from './ChatMessage.js';
import { FreeTextInput } from './FreeTextInput.js';
import { SuggestionChips } from './SuggestionChips.js';
import { Button } from '../common/Button.js';

const GREETING = "Hi! Tell me about the plant you found — or attach a photo.";

/** Reads a File into a full data URL (e.g. "data:image/jpeg;base64,…"). */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * The conversational surface: scrolling message bubbles, the agent's suggestion
 * chips beneath the latest assistant turn, a "thinking…" indicator while a turn
 * is in flight, and the always-available composer (free text + attach photo).
 */
export function ChatPanel(): JSX.Element {
  const messages = useWizardStore((s) => s.messages);
  const chips = useWizardStore((s) => s.chips);
  const phase = useWizardStore((s) => s.phase);
  const sendTurn = useWizardStore((s) => s.sendTurn);

  const awaiting = phase === 'awaiting';
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const jumpToBottom = () => {
      el.scrollTop = el.scrollHeight - el.clientHeight;
    };

    jumpToBottom();
    const timeouts = [
      window.setTimeout(jumpToBottom, 0),
      window.setTimeout(jumpToBottom, 100),
      window.setTimeout(jumpToBottom, 300),
    ];
    const firstFrame = window.requestAnimationFrame(() => {
      jumpToBottom();
      window.requestAnimationFrame(jumpToBottom);
    });

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(jumpToBottom);
    if (contentRef.current) resizeObserver?.observe(contentRef.current);

    const mutationObserver = new MutationObserver(jumpToBottom);
    if (contentRef.current) {
      mutationObserver.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      for (const timeout of timeouts) window.clearTimeout(timeout);
      window.cancelAnimationFrame(firstFrame);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [messages.length, awaiting]);

  async function onPhotoChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    const dataUrl = await readAsDataUrl(file);
    const imageBase64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
    void sendTurn({ imageBase64, imageDataUrl: dataUrl });
  }

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden bg-paper-50">
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <div ref={contentRef} className="space-y-3">
          <ChatMessage from="bot" text={GREETING} />
          {messages.map((m, i) => (
            <ChatMessage
              key={`msg-${i}-${m.at}`}
              from={m.role === 'assistant' ? 'bot' : 'user'}
              text={m.text}
              imageDataUrl={m.imageDataUrl}
            />
          ))}
          {awaiting ? (
            <div className="flex justify-start">
              <div className="rounded-lg border border-sage-300 bg-paper-100 px-4 py-2 font-body text-sm italic text-bark-500">
                Thinking…
              </div>
            </div>
          ) : null}
          {!awaiting && chips.length > 0 ? (
            <SuggestionChips chips={chips} onPick={(label) => void sendTurn({ text: label })} disabled={awaiting} />
          ) : null}
        </div>
      </div>
      <div className="shrink-0 border-t border-bark-300/40 bg-paper-100 p-4">
        <div className="flex items-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            aria-label="Take a photo with the camera"
            title="Take a photo"
            disabled={awaiting}
            onClick={() => photoInputRef.current?.click()}
          >
            📷
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-label="Upload a photo from your device"
            title="Upload a photo"
            disabled={awaiting}
            onClick={() => uploadInputRef.current?.click()}
          >
            🖼️
          </Button>
          {/* Camera input: `capture` opens the device camera on mobile. */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(e) => void onPhotoChosen(e)}
          />
          {/* Upload input: no `capture`, so mobile shows the photo library /
              file picker and desktop shows a normal file dialog. */}
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => void onPhotoChosen(e)}
          />
          <div className="flex-1">
            <FreeTextInput onSubmit={(text) => void sendTurn({ text })} disabled={awaiting} />
          </div>
        </div>
      </div>
    </section>
  );
}
